import VoucherApi from 'api/Voucher'
import { compareDateAfter, compareDateBefore, findDayAfterToday, isSameDate } from 'utilities/date'

// A request failure can mean two different things for the code:
//   - deterministic client error (4xx, e.g. a 404 for a consumed/invalid/no-longer-owned voucher):
//     the code is genuinely invalid.
//   - transient failure (5xx, or a network/local error with no status): the server hiccuped, not the
//     code, so a still-valid discount shouldn't be silently dropped.
// Only the stacked-voucher update handoff distinguishes them (via keepOnTransientError); the
// search-form flow keeps its original "drop on any failure" behavior.
function isTransientRequestError(error) {
  const status = error?.status
  // No status = network / local exception (customFetch sets isLocalException) → transient.
  if (status == null) return true
  return status >= 500
}

// Checks whether a voucher coupon code is within its sale period as of today. Shared by the
// search-form and update-itinerary flows so the validity rule lives in one place.
//
// `keepOnTransientError` controls the failure behavior:
//   - false (default): any request failure drops the code. This is the original search-form behavior
//     and is left unchanged so non-stacked flows are unaffected.
//   - true: only a deterministic 4xx drops the code; a transient 5xx/network error keeps it and lets
//     the search decide. Used by the BW3-origin stacked-voucher update handoff.
export async function checkIsVoucherCouponWithinSalePeriod(
  voucherCouponCode,
  { keepOnTransientError = false } = {}
) {
  try {
    const { data } = await VoucherApi.getAppliedCouponCodeURL(voucherCouponCode)
    const { sale_start_date, sale_end_date } = data
    const saleStartDate = sale_start_date?.replace(/-/g, '/')
    const saleEndDate = sale_end_date?.replace(/-/g, '/')
    const today = findDayAfterToday(0)

    const isAfterOrEqualStart =
      !saleStartDate ||
      compareDateAfter(today, saleStartDate, 'YYYY/MM/DD') ||
      isSameDate(today, saleStartDate, 'YYYY/MM/DD')
    if (!isAfterOrEqualStart) return false

    const isBeforeOrEqualEnd =
      !saleEndDate ||
      compareDateBefore(today, saleEndDate, 'YYYY/MM/DD') ||
      isSameDate(today, saleEndDate, 'YYYY/MM/DD')
    if (!isBeforeOrEqualEnd) return false

    return true
  } catch (error) {
    return keepOnTransientError && isTransientRequestError(error)
  }
}
